import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';

import Files from 'components/Shared/Files';
import Button from 'uikit/Button';
import Icon from 'uikit/Icon';
import Menu, { MenuItem } from 'uikit/Menu';
import Tooltip from 'uikit/Tooltip';

import ChapterDelete from './ChapterDelete';
import Questions from '../Questions';
import styles from './ChapterCard.module.scss';

const cx = classNames.bind(styles);

const ChapterCard = ({
  chapter,
  className,
  course,
  handleChapterDuplicate,
  handleChapterExpandedToggle,
  handleChapterMove,
  handleChapterSelect,
  handleChapterUpdate,
  isExpanded,
  isSelected,
  t,
}) => (
  <Draggable draggableId={chapter.id} index={chapter.order}>
    {(providedChapter, snapshotChapter) => (
      <Droppable droppableId={chapter.id} type="question">
        {(providedQuestions, snapshotQuestions) => (
          // eslint-disable-next-line jsx-a11y/interactive-supports-focus, jsx-a11y/click-events-have-key-events
          <div
            aria-label={chapter.title}
            className={cx(
              'container',
              {
                isDragging: snapshotChapter.isDragging,
                isDraggingOver: snapshotQuestions.isDraggingOver,
                isSelected,
              },
              className,
            )}
            ref={providedChapter.innerRef}
            role="button"
            {...providedChapter.draggableProps}
            {...providedChapter.dragHandleProps}
          >
            <div
              ref={providedQuestions.innerRef}
              {...providedQuestions.droppableProps}
            >
              <div className={cx('chapter')}>
                <button
                  aria-label={
                    isExpanded
                      ? t(
                          'Course/Teacher/Editor/Finder/ChapterCard/aria-close-chapter',
                        )
                      : t(
                          'Course/Teacher/Editor/Finder/ChapterCard/aria-open-chapter',
                        )
                  }
                  className={cx('icon')}
                  onClick={handleChapterExpandedToggle}
                  type="button"
                >
                  <Icon
                    name={isExpanded ? 'chevron-down' : 'chevron-right'}
                    variant="solid"
                  />
                </button>
                <div className={cx('chapter-icon')}>
                  {snapshotChapter.isDragging ? (
                    <Icon name="sort" />
                  ) : chapter.title ===
                    t(
                      'Course/Teacher/QuestionCreateFromIA/ia-chapter-title',
                    ) ? (
                    <Icon className={cx('ia-icon')} name="book-sparkles" />
                  ) : (
                    <Icon name="folder" />
                  )}
                </div>
                {chapter.questions.some((question) =>
                  question.comments.some((comment) => !comment.isRead),
                ) && <div className={cx('comment-notification')} />}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                <p
                  className={cx('title')}
                  onClick={handleChapterSelect}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                  role="button"
                >
                  {chapter.title}
                </p>
                {chapter.isHidden && (
                  <Tooltip
                    tooltip={t(
                      'Course/Teacher/Editor/Finder/ChapterCard/hidden',
                    )}
                  >
                    <Icon className={cx('hidden')} name="eye-slash" />
                  </Tooltip>
                )}
                {chapter.isLinked && (
                  <Tooltip
                    tooltip={t(
                      'Course/Teacher/Editor/Finder/ChapterCard/linked',
                    )}
                  >
                    <Icon className={cx('linked')} name="link" />
                  </Tooltip>
                )}
                <Menu
                  placement="bottom-end"
                  toggle={
                    <Button
                      className={cx('action')}
                      icon="ellipsis-v"
                      size="small"
                      tooltip={t(
                        'Course/Teacher/Editor/Finder/ChapterCard/options',
                      )}
                    />
                  }
                >
                  <MenuItem icon="edit" onClick={handleChapterUpdate}>
                    {t(
                      'Course/Teacher/Editor/Finder/ChapterCard/update-chapter',
                    )}
                  </MenuItem>
                  <MenuItem
                    icon="arrow-square-right"
                    onClick={handleChapterMove}
                  >
                    {t('Course/Teacher/Editor/Finder/ChapterCard/move-chapter')}
                  </MenuItem>
                  <MenuItem icon="copy" onClick={handleChapterDuplicate}>
                    {t(
                      'Course/Teacher/Editor/Finder/ChapterCard/duplicate-chapter',
                    )}
                  </MenuItem>
                  <ChapterDelete chapter={chapter} />
                </Menu>
              </div>
              {isExpanded && (
                <>
                  <Files chapterId={chapter.id} course={course} isTeacher />
                  <div
                    className={cx('questions', {
                      isDraggingOver: snapshotQuestions.isDraggingOver,
                    })}
                  >
                    <Questions chapterId={chapter.id} course={course} />
                    {providedQuestions.placeholder}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Droppable>
    )}
  </Draggable>
);

ChapterCard.propTypes = {
  chapter: PropTypes.object.isRequired,
  className: PropTypes.string,
  course: PropTypes.object.isRequired,
  handleChapterDuplicate: PropTypes.func.isRequired,
  handleChapterExpandedToggle: PropTypes.func.isRequired,
  handleChapterMove: PropTypes.func.isRequired,
  handleChapterSelect: PropTypes.func.isRequired,
  handleChapterUpdate: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

ChapterCard.defaultProps = {
  className: undefined,
};

export default ChapterCard;
